import produce from "immer";
import { createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Booking, Comment } from "../../../Types/UnitFeatureTypes";


export interface FetchBookingInfoState {
  request: {
      loading: boolean;
      error: string | null;
      singleBooking: Booking | null;
      comments: Comment[] | null;
      data: Booking[] | null;
      count: string | null;
  };
}

export enum Types {
  FETCH_BOOKING_REQUEST = "FETCH_BOOKING_REQUEST",
  FETCH_BOOKING_SUCCESS = "FETCH_BOOKING_SUCCESS",
  FETCH_BOOKING_FAILURE = "FETCH_BOOKING_FAILURE",
  FETCH_BOOKING_COMMENT_REQUEST = "FETCH_BOOKING_COMMENT_REQUEST",
  FETCH_BOOKING_COMMENT_SUCCESS = "FETCH_BOOKING_COMMENT_SUCCESS",
  FETCH_BOOKING_COMMENT_FAILURE = "FETCH_BOOKING_COMMENT_FAILURE",
}

const bookingActions = createAsyncAction(
  Types.FETCH_BOOKING_REQUEST,
  Types.FETCH_BOOKING_SUCCESS,
  Types.FETCH_BOOKING_FAILURE,
)
<
  { id?: number, offset?: number, sortColumn?: string, sortDirection?: string },
  { bookings?: Booking[], booking?: Booking, count: string },
  { message: string }
>();

const bookingCommentActions = createAsyncAction(
  Types.FETCH_BOOKING_COMMENT_REQUEST,
  Types.FETCH_BOOKING_COMMENT_SUCCESS,
  Types.FETCH_BOOKING_COMMENT_FAILURE,
)
<
  { id: number },
  { comments: Comment[] },
  { message: string }
>();

export const Actions = { bookingActions, bookingCommentActions };

export const initialState: FetchBookingInfoState = {
  request: {
    loading: false,
    error: null as string | null,
    singleBooking: null,
    comments: null,
    data: null,
    count: '0'
  }
};

export type FetchBookingState = typeof initialState;

const fetchBookingRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.data = null;

    return draft;
  });

const fetchBookingSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.bookingActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.data = payload.bookings ?? null;
    draft.request.singleBooking = payload.booking ?? null;
    draft.request.count = payload.count;
    if (draft.request.data) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchBookingFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.bookingActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchBookingCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.comments = null;

    return draft;
  });

const fetchBookingCommentSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.bookingCommentActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.comments = payload.comments;
    if (draft.request.comments) {
      draft.request.loading = false;
    }
  });

const fetchBookingCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.bookingCommentActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });


export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.bookingActions.request, fetchBookingRequest)
  .handleAction(Actions.bookingActions.success, fetchBookingSuccess)
  .handleAction(Actions.bookingActions.failure, fetchBookingFailure)
  .handleAction(Actions.bookingCommentActions.request, fetchBookingCommentRequest)
  .handleAction(Actions.bookingCommentActions.success, fetchBookingCommentSuccess)
  .handleAction(Actions.bookingCommentActions.failure, fetchBookingCommentFailure);
  